import Naluri from '~/common/api/naluri';
import Cookie from '~/common/cookie';
import { Socket } from 'phoenix';
import { sortWith, descend, prop, groupBy } from 'ramda';
import {
  COMMON_GET_CHANNELS_BEGIN,
  COMMON_GET_CHANNELS_SUCCESS,
  COMMON_GET_CHANNELS_FAILURE,
  COMMON_GET_CHANNELS_DISMISS_ERROR,
  COMMON_SIDEBAR_UPDATE_UNREAD,
  COMMON_SIDEBAR_RESET_UNREAD,
  COMMON_CONNECT_CHAT_MENU_SOCKET_BEGIN,
  COMMON_CONNECT_CHAT_MENU_SOCKET_SUCCESS,
  COMMON_USERS_JOINED,
  COMMON_USERS_LEFT,
  COMMON_SIDEBAR_UPDATE,
  COMMON_SIDEBAR_USER_STATUS_UPDATE,
  COMMON_UPDATE_USERS,
  COMMON_UPDATE_FILTER_PARAMS,
  COMMON_GET_CHANNEL_SUCCESS,
} from './constants';
import { getApiHost, getAppSecure } from '~/common/utils';
import { uniqBy } from 'lodash';
import moment from 'moment';

// Rekit uses redux-thunk for async actions by default: https://github.com/gaearon/redux-thunk
// If you prefer redux-saga, you can use rekit-plugin-redux-saga: https://github.com/supnate/rekit-plugin-redux-saga
export function getChannels(
  pageNr = 1,
  timeHorizonLabel = 'users',
  { paid = true, vip = false, to_reply = false, sponsors = [] },
) {
  return (dispatch, getState) => {
    // optionally you can have getState as the second argument
    dispatch({
      type: COMMON_GET_CHANNELS_BEGIN,
    });

    // Return a promise so that you could control UI flow without states in the store.
    // For example: after submit a form, you need to redirect the page to another when succeeds or show some errors message if fails.
    // It's hard to use state to manage it, but returning a promise allows you to easily achieve it.
    // e.g.: handleSubmit() { this.props.actions.submitForm(data).then(()=> {}).catch(() => {}); }
    const promise = new Promise((resolve, reject) => {
      // doRequest is a placeholder Promise. You should replace it with your own logic.
      // See the real-word example at:  https://github.com/supnate/rekit/blob/master/src/features/home/redux/fetchRedditReactjsList.js
      // args.error here is only for test coverage purpose.

      const page = pageNr;
      const selectedLabel = timeHorizonLabel;
      const params = {
        page: page,
        label: selectedLabel,
        paid,
        vip,
        to_reply,
        sponsors,
      };

      const doRequest = Naluri.get(`/api/expert/channels_v2`, params);
      doRequest.then(
        res => {
          dispatch({
            type: COMMON_GET_CHANNELS_SUCCESS,
            data: res,
            pageNr: pageNr,
          });
          resolve(res);
        },
        // Use rejectHandler as the second argument so that render errors won't be caught.
        err => {
          dispatch({
            type: COMMON_GET_CHANNELS_FAILURE,
            data: { error: err },
          });
          reject(err);
        },
      );
    });

    return promise;
  };
}

export function connectToChatMenu() {
  return (dispatch, getState) => {
    dispatch({
      type: COMMON_CONNECT_CHAT_MENU_SOCKET_BEGIN,
    });

    const token = Cookie.get('__naluri__');
    const userId = getState().session.authenticated.id;
    const socket = new Socket(getAppSecure('websocket') + getApiHost() + '/socket', {
      params: { token },
    });
    const channel = socket.channel('chat_menu:' + userId, {});

    socket.connect();

    channel.join().receive('ok', resp => {
      dispatch({
        type: COMMON_CONNECT_CHAT_MENU_SOCKET_SUCCESS,
        data: channel,
      });
    });
  };
}

const filterUsers = (users, channels) => {
  const byUnread = sortWith([descend(prop('unreads_count'))]);
  const byStatus = sortWith([
    (a, b) => {
      const u1 = users.find(u => u.id === a.id);
      const u2 = users.find(u => u.id === b.id);
      const l1 = u1.status ? u1.status.length : 0;
      const l2 = u2.status ? u2.status.length : 0;

      return l2 - l1;
    },
  ]);

  const groupByStatusAndUnread = groupBy(a => {
    return a.unreads_count === 0 ? 'unread0' : a.status === 'online' ? 'online' : 'offline';
  });

  let onlineUsers = [];
  let offlineUsers = [];
  let unreadUsers = [];

  const groupedUsers = groupByStatusAndUnread(users);

  Object.keys(groupedUsers).forEach(key => {
    if (key === 'online') {
      onlineUsers = byUnread(groupedUsers[key]);
    } else if (key === 'offline') {
      offlineUsers = byUnread(groupedUsers[key]);
    } else {
      unreadUsers = byStatus(groupedUsers[key]);
    }
  });

  return [...onlineUsers, ...offlineUsers, ...unreadUsers];
};

const assignData = (users, channels) => {
  return users.map(user => {
    const channel = channels.find(c => c.id === user.cid);
    const last_message = channel
      ? channel.last_message
        ? channel.last_message.timestamp
        : Date.now()
      : Date.now();

    return {
      ...user,
      c_reply: channel ? channel.to_reply : false,
      c_type: channel ? channel.type : null,
      archived_status: channel ? channel.archived_status : false,
      last_message,
    };
  });
};

const filterDuplicateData = (originalData, iteratee) => {
  return uniqBy(originalData, iteratee);
};

// Async action saves request error by default, this method is used to dismiss the error info.
// If you don't want errors to be saved in Redux store, just ignore this method.
export function dismissGetChannelsError() {
  return {
    type: COMMON_GET_CHANNELS_DISMISS_ERROR,
  };
}

export const updateFilterParams = (paid, vip, to_reply, sponsors) => {
  return {
    type: COMMON_UPDATE_FILTER_PARAMS,
    payload: {
      paid,
      vip,
      to_reply,
      sponsors,
    },
  };
};

export function reducer(state, action) {
  switch (action.type) {
    case COMMON_UPDATE_FILTER_PARAMS:
      return {
        ...state,
        filterParams: {
          ...state.filterParams,
          paid: action.payload.paid,
          to_reply: action.payload.to_reply,
          vip: action.payload.vip,
          sponsors: action.payload.sponsors,
        },
      };

    case COMMON_GET_CHANNELS_BEGIN:
      // Just after a request is sent
      return {
        ...state,
        getChannelsError: null,
        getChannelsPending: true,
      };

    case COMMON_GET_CHANNEL_SUCCESS:
    case COMMON_GET_CHANNELS_SUCCESS:
      // The request is success
      // Concatenate the users list if the request is asking for page number after 1
      let usersChannels;

      if (action.pageNr > 1) {
        usersChannels = state.users.concat(action.data.users);
      } else {
        usersChannels = action.data.users;
      }

      const channels = state.channels.concat(action.data.channels);

      const filteredUsersChannels = filterDuplicateData(usersChannels, 'cid');
      const filteredChannels = filterDuplicateData(channels, 'id');

      let sponsors = state.sponsors;
      if (action.data.sponsors) {
        action.data.sponsors.forEach(s => {
          if (!sponsors.includes(s) && s != null) sponsors.push(s);
        });
      }

      const assigned = assignData(filteredUsersChannels, filteredChannels);
      const filtered = filterUsers(assigned, filteredChannels);

      let pagination = state.pagination;
      if (action.data.pagination) {
        pagination = action.data.pagination;
      }

      return {
        ...state,
        users: filteredUsersChannels,
        channels: filteredChannels,
        sponsors: sponsors,
        pagination: pagination,
        filteredUserList: filtered,
        timeHorizonLabel: action.data.label,
        getChannelsPending: false,
        getChannelsError: null,
        apiSentRequestTime: moment(),
      };

    case COMMON_GET_CHANNELS_FAILURE:
      // The request is failed
      return {
        ...state,
        getChannelsPending: false,
        getChannelsError: action.data.error,
      };

    case COMMON_GET_CHANNELS_DISMISS_ERROR:
      // Dismiss the request failure error
      return {
        ...state,
        getChannelsError: null,
      };

    case COMMON_SIDEBAR_UPDATE_UNREAD:
      const updatedUsers = state.filteredUserList.map(user => {
        if (user.cid === action.data) {
          const prevCount = user.unreads_count;
          return Object.assign(user, { unreads_count: prevCount + 1 });
        } else {
          return user;
        }
      });

      const newUsersUpdated = state.newUsers.map(user => {
        if (user.cid === action.data) {
          const prevCount = user.unreads_count;
          return Object.assign(user, { unreads_count: prevCount + 1 });
        } else {
          return user;
        }
      });

      const recentlyReassignedUsersUpdated = state.recentlyReassignedUsers.map(user => {
        if (user.cid === action.data) {
          const prevCount = user.unreads_count;
          return Object.assign(user, { unreads_count: prevCount + 1 });
        } else {
          return user;
        }
      });

      const groupsUpdated = state.groups.map(group => {
        if (group.cid === action.data) {
          const prevCount = group.unreads_count;
          return Object.assign(group, { unreads_count: prevCount + 1 });
        } else {
          return group;
        }
      });

      return {
        ...state,
        filteredUserList: updatedUsers,
        newUsers: newUsersUpdated,
        recentlyReassignedUsers: recentlyReassignedUsersUpdated,
        groups: groupsUpdated,
        users: updatedUsers,
        recentlyReassignedUsersChannels: recentlyReassignedUsersUpdated,
        newUsersChannels: newUsersUpdated,
      };

    case COMMON_SIDEBAR_RESET_UNREAD:
      const usersUpdated = state.users.map(user => {
        if (user.cid === action.data) {
          return Object.assign(user, { unreads_count: 0 });
        } else {
          return user;
        }
      });

      const updatedFilteredUser = state.filteredUserList.map(user => {
        if (user.cid === action.data) {
          return Object.assign(user, { unreads_count: 0 });
        } else {
          return user;
        }
      });

      const updatedNewUsers = state.newUsers.map(user => {
        if (user.cid === action.data) {
          return Object.assign(user, { unreads_count: 0 });
        } else {
          return user;
        }
      });

      const updatedRecentlyReassignedUsers = state.recentlyReassignedUsers.map(user => {
        if (user.cid === action.data) {
          return Object.assign(user, { unreads_count: 0 });
        } else {
          return user;
        }
      });

      const updatedGroups = state.groups.map(group => {
        if (group.cid === action.data) {
          return Object.assign(group, { unreads_count: 0 });
        } else {
          return group;
        }
      });

      return {
        ...state,
        filteredUserList: updatedFilteredUser,
        users: usersUpdated,
        newUsers: updatedNewUsers,
        recentlyReassignedUsers: updatedRecentlyReassignedUsers,
        groups: updatedGroups,
        recentlyReassignedUsersChannels: updatedRecentlyReassignedUsers,
        newUsersChannels: updatedNewUsers,
      };

    case COMMON_CONNECT_CHAT_MENU_SOCKET_BEGIN:
      return {
        ...state,
        chatMenuPending: true,
      };

    case COMMON_CONNECT_CHAT_MENU_SOCKET_SUCCESS:
      return {
        ...state,
        chatMenuPending: false,
        chatMenuChannel: action.data,
      };

    case COMMON_USERS_JOINED:
      if (action.payload.length === 0) return state;

      const usersJoined = state.filteredUserList.map(user => {
        if (action.payload.includes(user.id)) {
          return Object.assign(user, { status: 'online' });
        } else {
          return user;
        }
      });

      const newUsersJoined = state.newUsers.map(user => {
        if (action.payload.includes(user.id)) {
          return Object.assign(user, { status: 'online' });
        } else {
          return user;
        }
      });

      const recentlyReassignedUsersJoined = state.recentlyReassignedUsers.map(user => {
        if (action.payload.includes(user.id)) {
          return Object.assign(user, { status: 'online' });
        } else {
          return user;
        }
      });

      const filteredJoined = filterUsers(usersJoined, state.channels);
      const filteredNewUsersJoined = filterUsers(newUsersJoined, state.channels);
      const filteredRecentlyReassignedUsersJoined = filterUsers(
        recentlyReassignedUsersJoined,
        state.channels,
      );

      return {
        ...state,
        filteredUserList: filteredJoined,
        newUsers: filteredNewUsersJoined,
        recentlyReassignedUsers: filteredRecentlyReassignedUsersJoined,
        users: filteredJoined,
      };

    case COMMON_USERS_LEFT:
      if (action.payload.length === 0) return state;

      const usersLeft = state.filteredUserList.map(user => {
        if (action.payload.includes(user.id)) {
          return Object.assign(user, { status: null });
        } else {
          return user;
        }
      });

      const newUsersLeft = state.newUsers.map(user => {
        if (action.payload.includes(user.id)) {
          return Object.assign(user, { status: null });
        } else {
          return user;
        }
      });

      const recentlyReassignedUsersLeft = state.recentlyReassignedUsers.map(user => {
        if (action.payload.includes(user.id)) {
          return Object.assign(user, { status: null });
        } else {
          return user;
        }
      });

      const filteredLeft = filterUsers(usersLeft, state.channels);
      const filteredNewUsersLeft = filterUsers(newUsersLeft, state.channels);
      const filteredRecentlyReassignedUsersLeft = filterUsers(
        recentlyReassignedUsersLeft,
        state.channels,
      );

      return {
        ...state,
        filteredUserList: filteredLeft,
        newUsers: filteredNewUsersLeft,
        recentlyReassignedUsers: filteredRecentlyReassignedUsersLeft,
        users: filteredLeft,
      };

    case COMMON_SIDEBAR_UPDATE:
      const prevUsers = state.filteredUserList.filter(user => user.cid !== action.channelId);
      const updatedUser = state.filteredUserList.filter(user => user.cid === action.channelId);

      return {
        ...state,
        filteredUserList: [...updatedUser, ...prevUsers],
        users: [...updatedUser, ...prevUsers],
      };

    case COMMON_SIDEBAR_USER_STATUS_UPDATE:
      if (!action.payload) return state;

      const newUsers = state.newUsers
        ? state.newUsers.map(usr =>
            usr.id === action.payload.id
              ? Object.assign({}, usr, { vip: action.payload.vip })
              : usr,
          )
        : [];

      const filteredUserList = state.filteredUserList
        ? state.filteredUserList.map(usr =>
            usr.id === action.payload.id
              ? Object.assign({}, usr, { vip: action.payload.vip })
              : usr,
          )
        : [];

      const recentlyReassignedUsers = state.recentlyReassignedUsers
        ? state.recentlyReassignedUsers.map(usr =>
            usr.id === action.payload.id
              ? Object.assign({}, usr, { vip: action.payload.vip })
              : usr,
          )
        : [];

      return {
        ...state,
        newUsers: newUsers,
        filteredUserList: filteredUserList,
        recentlyReassignedUsers: recentlyReassignedUsers,
      };

    case COMMON_UPDATE_USERS:
      return {
        ...state,
        filteredUserList: action.payload.userChannels,
        users: action.payload.userChannels,
        channels: action.payload.channels,
      };

    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/features/common/redux/getChannels.js